import React, { createContext, useContext, useState, useCallback, useRef, useMemo } from 'react';
import { useApp } from './AppContext';
import { useFilters } from './FilterContext';

const ReportExportContext = createContext(null);

export const ReportExportProvider = ({ children }) => {
  const { currentPlant, currentArea, currentRole } = useApp();
  const { plant, area, timeRange } = useFilters();
  const sectionsRef = useRef(new Map());
  const [sectionIds, setSectionIds] = useState([]);

  const registerSection = useCallback(({ id, title, description, elementRef, exportHint }) => {
    // Only update if section doesn't exist or has changed
    const existing = sectionsRef.current.get(id);
    const newSection = { id, title, description, elementRef, exportHint };
    
    // Check if section actually changed
    if (existing && 
        existing.title === title && 
        existing.description === description &&
        existing.elementRef === elementRef) {
      return; // No change, skip update
    }

    sectionsRef.current.set(id, newSection);
    
    setSectionIds(prev => {
      if (prev.includes(id)) {
        return prev; // Already registered
      }
      return [...prev, id];
    });
  }, []);

  const unregisterSection = useCallback((id) => {
    sectionsRef.current.delete(id);
    setSectionIds(prev => prev.filter(sid => sid !== id));
  }, []);

  const getSection = useCallback((id) => {
    return sectionsRef.current.get(id);
  }, []);

  // Memoize sections array to prevent unnecessary re-renders
  const sections = useMemo(() => {
    return sectionIds
      .map(id => sectionsRef.current.get(id))
      .filter(Boolean)
      .sort((a, b) => {
        // Try to sort by DOM order
        if (!a.elementRef?.current || !b.elementRef?.current) return 0;
        try {
          const position = a.elementRef.current.compareDocumentPosition(b.elementRef.current);
          return position & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
        } catch {
          return 0;
        }
      });
  }, [sectionIds]);

  const getPageMeta = useCallback(() => {
    return {
      plant: currentPlant?.name || plant || 'N/A',
      area: currentArea?.name || area || 'All Areas',
      role: currentRole?.name || 'N/A',
      timestamp: new Date().toLocaleString('en-IN', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),
      timeRange: timeRange || '30days'
    };
  }, [currentPlant, currentArea, currentRole, plant, area, timeRange]);

  const value = useMemo(() => ({
    sections,
    registerSection,
    unregisterSection,
    getSection,
    getPageMeta
  }), [sections, registerSection, unregisterSection, getSection, getPageMeta]);

  return (
    <ReportExportContext.Provider value={value}>
      {children}
    </ReportExportContext.Provider>
  );
};

export const useReportExport = () => {
  const context = useContext(ReportExportContext);
  if (!context) {
    throw new Error('useReportExport must be used within a ReportExportProvider');
  }
  return context;
};

export default ReportExportContext;
